import type { TenantConfig } from './TenantContext.ts';

function stripTrailingSlashes(s: string): string {
  let end = s.length;
  while (end > 0 && s.codePointAt(end - 1) === 47 /* / */) {
    end -= 1;
  }
  return s.slice(0, end);
}

export function parseEnvMapValue(
  env: Record<string, unknown> | null | undefined,
  key: string
): string | undefined {
  if (env == null) return undefined;
  const v = env[key];
  if (v === undefined || v === null) return undefined;
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'bigint' || typeof v === 'symbol') {
    return String(v);
  }
  return undefined;
}

/** `USE_LOCAL_SQLITE` injected into the bundle: missing, empty, or `"0"` → no local SQLite. */
export function parseUseLocalSqliteFlag(value: string | undefined): boolean {
  if (value === undefined || value.trim() === '') return false;
  return value.trim() !== '0';
}

export function normalizeBrowserApiBaseUrl(url: string): string {
  const trimmed = url.trim();
  if (/api:\d+/.test(trimmed) || /:\/\/api:/.test(trimmed)) {
    throw new Error(
      'API_URL cannot use an internal service hostname (api:PORT); the browser cannot resolve it. ' +
        `Use a URL reachable from the client (e.g. http://localhost:…), not ${trimmed}.`,
    );
  }
  return stripTrailingSlashes(trimmed).replace(/\/api$/, '');
}

export function resolveAppDisplayTitle(
  adminAppTitle: string | undefined,
  adminTenantName: string | undefined
): string {
  return adminAppTitle?.trim() || adminTenantName?.trim() || 'Paquetes';
}

export function tryReadEnvValue(
  readEnv: () => Record<string, unknown> | undefined,
  key: string
): string | undefined {
  try {
    return parseEnvMapValue(readEnv(), key);
  } catch {
    return undefined;
  }
}

export const appConfigEnvKeyReader = {
  read(key: string): string | undefined {
    return tryReadEnvValue(() => import.meta.env, key);
  },
};

export function readAppConfigEnvKey(key: string): string | undefined {
  return appConfigEnvKeyReader.read(key);
}

/** Same-origin Vite dev proxy paths (see inclusionSocialDevProxyPlugin.mjs). */
export const INCLUSION_SOCIAL_DEV_CATALOG_PROXY_PATH = '/dev/inclusion-social/catalog-pull';
export const INCLUSION_SOCIAL_DEV_PUSH_PROXY_PATH = '/dev/inclusion-social/push';

export function resolveInclusionSocialDevProxyUrl(path: string): string | null {
  if (import.meta.env.MODE !== 'development') return null;
  const origin = globalThis.location?.origin;
  if (typeof origin !== 'string' || !origin.length) return null;
  return `${origin}${path}`;
}

function resolveSyncMockBaseUrlForConfig(apiBaseUrl: string): string {
  if ((appConfigEnvKeyReader.read('ADMIN_SYNC_MOCK') ?? '1') !== '0') {
    const origin = globalThis.location?.origin;
    if (typeof origin === 'string' && origin.length > 0) return origin;
  }
  return stripTrailingSlashes(apiBaseUrl.trim());
}

/** Inclusion Social push query (`yearpostulacion`); defaults to current calendar year. */
export function appendSyncPushQueryParams(url: string, yearpostulacion: string): string {
  const year = yearpostulacion.trim();
  if (!year) return url;
  try {
    const base = globalThis.location?.origin ?? 'http://localhost';
    const parsed = new URL(url, base);
    parsed.searchParams.set('yearpostulacion', year);
    if (/^https?:\/\//i.test(url)) return parsed.toString();
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return url;
  }
}

/** Application config from `import.meta.env` (no feature modules). */
export class AppConfig {
  private static apiBaseUrlMemo: string | null = null;

  static getApiBaseUrl(): string {
    if (this.apiBaseUrlMemo === null) {
      const raw = appConfigEnvKeyReader.read('API_URL')?.trim() || 'http://localhost:3000';
      this.apiBaseUrlMemo = normalizeBrowserApiBaseUrl(raw);
    }
    return this.apiBaseUrlMemo;
  }

  /** Sync/catalog HTTP base; mock mode uses same-origin when ADMIN_SYNC_MOCK is enabled. */
  static getSyncApiBaseUrl(): string {
    if ((appConfigEnvKeyReader.read('ADMIN_SYNC_MOCK') ?? '1') !== '0') {
      const origin = globalThis.location?.origin;
      if (typeof origin === 'string' && origin.length > 0) return origin;
    }
    return this.getApiBaseUrl().replace(/\/$/, '');
  }

  /** True when catalog pull uses same-origin Vite mock (`/sync/pull`). */
  static isSyncMockEnabled(): boolean {
    return (appConfigEnvKeyReader.read('ADMIN_SYNC_MOCK') ?? '1') !== '0';
  }

  /** Full URL for remote catalog pull (Inclusion Social); unset → `/sync/pull` mock/stub. */
  static getCatalogPullUrl(apiBaseUrl: string = this.getApiBaseUrl()): string {
    if ((appConfigEnvKeyReader.read('ADMIN_SYNC_MOCK') ?? '1') !== '0') {
      return `${resolveSyncMockBaseUrlForConfig(apiBaseUrl)}/sync/pull`;
    }
    const override = appConfigEnvKeyReader.read('ADMIN_CATALOG_PULL_URL')?.trim();
    if (override) {
      return (
        resolveInclusionSocialDevProxyUrl(INCLUSION_SOCIAL_DEV_CATALOG_PROXY_PATH) ?? override
      );
    }
    return `${resolveSyncMockBaseUrlForConfig(apiBaseUrl)}/sync/pull`;
  }

  /** Full URL for record push (POST); mock → `/sync/push`. Falls back to the catalog pull URL. */
  static getCatalogPushUrl(apiBaseUrl: string = this.getApiBaseUrl()): string {
    if ((appConfigEnvKeyReader.read('ADMIN_SYNC_MOCK') ?? '1') !== '0') {
      return `${resolveSyncMockBaseUrlForConfig(apiBaseUrl)}/sync/push`;
    }
    const override =
      appConfigEnvKeyReader.read('ADMIN_SYNC_PUSH_URL')?.trim() ||
      appConfigEnvKeyReader.read('ADMIN_CATALOG_PULL_URL')?.trim();
    let url: string;
    if (override) {
      url =
        resolveInclusionSocialDevProxyUrl(INCLUSION_SOCIAL_DEV_PUSH_PROXY_PATH) ?? override;
    } else {
      url = `${resolveSyncMockBaseUrlForConfig(apiBaseUrl)}/sync/push`;
    }
    return appendSyncPushQueryParams(url, this.getSyncYearpostulacion());
  }

  /** Postulación year query param for Inclusion Social push; override via `ADMIN_SYNC_YEARPOSTULACION`. */
  static getSyncYearpostulacion(): string {
    const override = appConfigEnvKeyReader.read('ADMIN_SYNC_YEARPOSTULACION')?.trim();
    if (override) return override;
    return String(new Date().getFullYear());
  }

  static getTokenKey(): string {
    return appConfigEnvKeyReader.read('ADMIN_TOKEN_STORAGE_KEY')?.trim() || 'admin_access_token';
  }

  /** Service Bearer token for the pre-login catalog pull (`/login`, unauthenticated). Empty → none. */
  static getCatalogPullToken(): string {
    return appConfigEnvKeyReader.read('BEARER_TOKEN')?.trim() || '';
  }

  static getFilesPublicPrefix(): string {
    return appConfigEnvKeyReader.read('ADMIN_FILES_PUBLIC_PREFIX')?.trim() || '/storage';
  }

  static getLocalSqliteIdbName(): string {
    return appConfigEnvKeyReader.read('LOCAL_SQLITE_IDB_NAME')?.trim() || 'esan-paquetes-local';
  }

  static getLocalSqliteIdbVersion(): number {
    const raw = appConfigEnvKeyReader.read('LOCAL_SQLITE_IDB_VERSION')?.trim();
    if (!raw) return 1;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
  }

  static getLocalSqliteIdbStore(): string {
    return appConfigEnvKeyReader.read('LOCAL_SQLITE_IDB_STORE')?.trim() || 'kv';
  }

  static getLocalSqliteIdbSqliteKey(): string {
    return appConfigEnvKeyReader.read('LOCAL_SQLITE_IDB_KEY')?.trim() || 'sqlite';
  }

  static getAppDisplayTitle(): string {
    return resolveAppDisplayTitle(
      appConfigEnvKeyReader.read('ADMIN_APP_TITLE'),
      appConfigEnvKeyReader.read('ADMIN_TENANT_NAME'),
    );
  }

  static useLocalSqliteClient(): boolean {
    return parseUseLocalSqliteFlag(appConfigEnvKeyReader.read('USE_LOCAL_SQLITE'));
  }

  static isEntregaGpsRequired(): boolean {
    return (appConfigEnvKeyReader.read('ADMIN_ENTREGA_GPS_REQUIRED') ?? '1') !== '0';
  }

  static getEntregaPdfContrato(): string {
    return appConfigEnvKeyReader.read('ADMIN_ENTREGA_PDF_CONTRATO')?.trim() || '4600105329';
  }

  static getEntregaPdfContratista(): string {
    return (
      appConfigEnvKeyReader.read('ADMIN_ENTREGA_PDF_CONTRATISTA')?.trim() ||
      'RODOLFO EUCARIO GÓMEZ ZULUAGA'
    );
  }

  static getEntregaPdfTemplateUrl(): string {
    return (
      appConfigEnvKeyReader.read('ADMIN_ENTREGA_PDF_TEMPLATE_URL')?.trim() ||
      '/templates/fo-gesr-entrega-template.pdf'
    );
  }

  static getReposicionPdfTemplateUrl(): string {
    return (
      appConfigEnvKeyReader.read('ADMIN_REPOSICION_PDF_TEMPLATE_URL')?.trim() ||
      '/templates/fo-gesr-reposicion-template.pdf'
    );
  }

  static resetApiBaseUrlCacheForTests(): void {
    this.apiBaseUrlMemo = null;
  }

  static getLocalSqliteSnapshotUrl(): string {
    return appConfigEnvKeyReader.read('LOCAL_SQLITE_DB_URL')?.trim() || '/database/local.db';
  }

  static getDefaultTenantConfig(): TenantConfig {
    return {
      id: appConfigEnvKeyReader.read('ADMIN_TENANT_ID')?.trim() || 'default-tenant',
      name: appConfigEnvKeyReader.read('ADMIN_TENANT_NAME')?.trim() || 'Default tenant',
      apiUrl: this.getApiBaseUrl(),
      theme: {
        primaryColor: appConfigEnvKeyReader.read('ADMIN_THEME_PRIMARY')?.trim() || '#0d3d66',
        secondaryColor: appConfigEnvKeyReader.read('ADMIN_THEME_SECONDARY')?.trim() || '#0d6b47',
      },
    };
  }
}
